import React, { useEffect, useState } from 'react';
import * as mentorApi from '../api/mentorApi';

const MARKING_ROLES = ['spoc', 'counselor', 'program_administrator', 'auditor'];

/**
 * Self-contained attendance module for a completed session, scoped to one cohort.
 * Mounted inside SessionDetail.js only — no App.js state, no shared props beyond
 * session/currentUser/cohortId, so it can be added/removed without touching the
 * rest of the app. Absentee-only model: roster checkbox = "mark absent".
 */
export default function SessionAttendance({ session, currentUser, cohortId, cohortLabel, showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [absentIds, setAbsentIds] = useState(new Set());
  const [error, setError] = useState(null);

  const canMark = MARKING_ROLES.includes(currentUser?.role);
  const isCompleted = String(session?.status || '').toLowerCase() === 'completed';

  useEffect(() => {
    if (!session?.id || !cohortId || !isCompleted) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    mentorApi.getSessionAttendance(session.id, cohortId)
      .then((data) => {
        if (cancelled) return;
        const roster = data?.students || [];
        setStudents(roster);
        setAbsentIds(new Set(roster.filter((s) => s.is_absent).map((s) => s.id)));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load attendance roster:', err);
        setError('Could not load attendance roster.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [session?.id, cohortId, isCompleted]);

  if (!isCompleted) {
    return null;
  }

  const toggleAbsent = (studentId) => {
    setAbsentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await mentorApi.putSessionAttendance(session.id, cohortId, Array.from(absentIds));
      const roster = data?.students || [];
      setStudents(roster);
      setAbsentIds(new Set(roster.filter((s) => s.is_absent).map((s) => s.id)));
      showToast?.('Attendance saved.');
    } catch (err) {
      console.error('Failed to save attendance:', err);
      showToast?.(err?.message || 'Failed to save attendance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
        Attendance{cohortLabel ? ` — ${cohortLabel}` : ''}
      </div>

      {loading ? (
        <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>Loading roster…</div>
      ) : error ? (
        <div style={{ color: '#b91c1c', fontSize: '13px' }}>{error}</div>
      ) : students.length === 0 ? (
        <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>No students in this cohort.</div>
      ) : (
        <>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Check a student to mark them absent. Unchecked students are recorded as present.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '6px' }}>
            {students.map((s) => (
              <label key={s.id} className="checklist-row-item" style={{ opacity: canMark ? 1 : 0.7 }}>
                <input
                  type="checkbox"
                  checked={absentIds.has(s.id)}
                  disabled={!canMark || saving}
                  onChange={() => toggleAbsent(s.id)}
                />
                <span className="checklist-row-label">{s.name || `Student #${s.id}`}</span>
                {absentIds.has(s.id) && <span className="checklist-row-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>Absent</span>}
              </label>
            ))}
          </div>
          {canMark ? (
            <button
              type="button"
              className="btn-secondary-action"
              style={{ marginTop: '14px' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Attendance'}
            </button>
          ) : (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
              Read-only — only SPOC, counselor, auditor, or PA can mark attendance.
            </div>
          )}
        </>
      )}
    </div>
  );
}
